/**
 * tableDesign.ts
 *
 * Brand-theme inheritance for Table widget design controls (#969 Part C —
 * header/cell styling, conditional formatting, significance treatment).
 * Mirrors chartDesign.ts's model exactly:
 *
 *   design-system default  --(explicit override)-->  TableDesign on the widget
 *
 * TABLE_DESIGN_DEFAULTS duplicates the values currently hardcoded in
 * platinum-table-new/styles.ts (COLORS.BG_GROUP, COLORS.BG_LABEL, etc.) —
 * same duplication chartDesign.ts accepts for CHART_DESIGN_DEFAULTS, and for
 * the same reason: this module is for the editor's controls (showing the
 * effective value and whether it's overridden); the render path
 * (styles.ts) patches only explicitly-overridden properties directly and
 * does not import from here, so an untouched table renders pixel-identical
 * (see the #968 Part C applyChartDesign lesson, commit 466740d3a).
 *
 * Conditional-formatting rules are a list, not a single property, so they
 * have their own small set of pure list helpers below rather than fitting
 * the single-property path model.
 *
 * All functions here are pure — no React, no fetch — so the merge, revert,
 * and rule-list logic is unit-testable without mounting anything. See
 * tableDesign.test.ts.
 */

import type {
  TableDesign,
  TableHeaderLevelDesign,
  TableCellDesign,
  TableCellAlignment,
  TableConditionalFormatRule,
  TableSignificanceDesign,
  TableSignificanceEmphasis,
} from '../types/gotypes'

export interface ResolvedHeaderLevelDesign {
  backgroundColor: string
  textColor: string
  fontWeight: number
}

export interface ResolvedTableCellDesign {
  fontSize: number
  alignment: TableCellAlignment
  borderColor: string
  borderWidth: number
  gridlinesVisible: boolean
}

export interface ResolvedTableSignificanceDesign {
  color: string
  emphasis: TableSignificanceEmphasis
}

export interface ResolvedTableDesign {
  header: {
    group: ResolvedHeaderLevelDesign
    label: ResolvedHeaderLevelDesign
  }
  cell: ResolvedTableCellDesign
  significance: ResolvedTableSignificanceDesign
}

/**
 * The design-system default — matches what platinum-table-new/styles.ts
 * hardcodes today (COLORS.BG_GROUP, COLORS.BG_LABEL, the default '#000'
 * text color, 12px cell font, centered alignment, the dark border color/
 * 1px width, and the significance band color #6c757d). Changing these
 * values changes the default look of every table that has never been
 * explicitly overridden.
 */
export const TABLE_DESIGN_DEFAULTS: ResolvedTableDesign = {
  header: {
    group: { backgroundColor: '#f8f9fa', textColor: '#000000', fontWeight: 400 },
    label: { backgroundColor: '#f1f3f5', textColor: '#000000', fontWeight: 400 },
  },
  cell: {
    fontSize: 12,
    alignment: 'center',
    borderColor: 'rgba(0, 0, 0, 0.10)',
    borderWidth: 1,
    gridlinesVisible: true,
  },
  significance: {
    color: '#6c757d',
    emphasis: 'none',
  },
}

function resolveHeaderLevel(
  defaults: ResolvedHeaderLevelDesign,
  override: TableHeaderLevelDesign | undefined,
): ResolvedHeaderLevelDesign {
  if (!override) return defaults
  return {
    backgroundColor: override.background_color ?? defaults.backgroundColor,
    textColor: override.text_color ?? defaults.textColor,
    fontWeight: override.font_weight ?? defaults.fontWeight,
  }
}

function resolveCell(defaults: ResolvedTableCellDesign, override: TableCellDesign | undefined): ResolvedTableCellDesign {
  if (!override) return defaults
  return {
    fontSize: override.font_size ?? defaults.fontSize,
    alignment: override.alignment ?? defaults.alignment,
    borderColor: override.border_color ?? defaults.borderColor,
    borderWidth: override.border_width ?? defaults.borderWidth,
    gridlinesVisible: override.gridlines_visible ?? defaults.gridlinesVisible,
  }
}

function resolveSignificance(
  defaults: ResolvedTableSignificanceDesign,
  override: TableSignificanceDesign | undefined,
): ResolvedTableSignificanceDesign {
  if (!override) return defaults
  return {
    color: override.color ?? defaults.color,
    emphasis: override.emphasis ?? defaults.emphasis,
  }
}

/** Merges a widget's stored TableDesign on top of the design-system defaults. */
export function resolveTableDesign(design: TableDesign | undefined): ResolvedTableDesign {
  return {
    header: {
      group: resolveHeaderLevel(TABLE_DESIGN_DEFAULTS.header.group, design?.header?.group),
      label: resolveHeaderLevel(TABLE_DESIGN_DEFAULTS.header.label, design?.header?.label),
    },
    cell: resolveCell(TABLE_DESIGN_DEFAULTS.cell, design?.cell),
    significance: resolveSignificance(TABLE_DESIGN_DEFAULTS.significance, design?.significance),
  }
}

/**
 * Every editable single-value design property (everything except the
 * conditional-formatting rule list, which has its own helpers below),
 * addressed the same way in the editor UI, `isTableDesignPropertyOverridden`,
 * `setTableDesignProperty`, and `revertTableDesignProperty` — one vocabulary
 * for "which control is this". Paths are the real nested JSON keys, so no
 * separate path->key lookup table is needed.
 */
export type TableDesignPropertyPath =
  | 'header.group.background_color'
  | 'header.group.text_color'
  | 'header.group.font_weight'
  | 'header.label.background_color'
  | 'header.label.text_color'
  | 'header.label.font_weight'
  | 'cell.font_size'
  | 'cell.alignment'
  | 'cell.border_color'
  | 'cell.border_width'
  | 'cell.gridlines_visible'
  | 'significance.color'
  | 'significance.emphasis'

type PathParts = [string, string, string] | [string, string]

function splitPath(path: TableDesignPropertyPath): PathParts {
  const parts = path.split('.')
  return parts as PathParts
}

type Section = Record<string, unknown>

/** True when this exact property carries an explicit widget-level override. */
export function isTableDesignPropertyOverridden(
  design: TableDesign | undefined,
  path: TableDesignPropertyPath,
): boolean {
  const parts = splitPath(path)
  let cur: Section | undefined = design as unknown as Section | undefined
  for (let i = 0; i < parts.length - 1; i++) {
    if (cur === undefined) return false
    cur = cur[parts[i]] as Section | undefined
  }
  if (cur === undefined) return false
  return cur[parts[parts.length - 1]] !== undefined
}

/** Sets one property, creating its section(s) if needed. Pure — returns a new TableDesign. */
export function setTableDesignProperty(
  design: TableDesign | undefined,
  path: TableDesignPropertyPath,
  value: string | number | boolean,
): TableDesign {
  const parts = splitPath(path)

  if (parts.length === 3) {
    const [a, b, key] = parts
    const sectionA = ((design as unknown as Section | undefined)?.[a] as Section | undefined) ?? {}
    const sectionB = (sectionA[b] as Section | undefined) ?? {}
    return {
      ...design,
      [a]: { ...sectionA, [b]: { ...sectionB, [key]: value } },
    } as TableDesign
  }

  const [a, key] = parts
  const sectionA = ((design as unknown as Section | undefined)?.[a] as Section | undefined) ?? {}
  return {
    ...design,
    [a]: { ...sectionA, [key]: value },
  } as TableDesign
}

/**
 * Clears one overridden property — the per-property "revert to theme"
 * control. Prunes each section that becomes empty, all the way up, so a
 * fully-reverted widget round-trips as "no design object" rather than a tree
 * of empty `{}`s (mirrors revertChartDesignProperty).
 */
export function revertTableDesignProperty(
  design: TableDesign | undefined,
  path: TableDesignPropertyPath,
): TableDesign | undefined {
  if (!design) return design
  const parts = splitPath(path)
  const root: Section = { ...(design as unknown as Section) }

  if (parts.length === 3) {
    const [a, b, key] = parts
    const sectionA = root[a] as Section | undefined
    const sectionB = sectionA?.[b] as Section | undefined
    if (!sectionB || sectionB[key] === undefined) return design

    const nextB: Section = { ...sectionB }
    delete nextB[key]

    const nextA: Section = { ...sectionA }
    if (Object.keys(nextB).length > 0) {
      nextA[b] = nextB
    } else {
      delete nextA[b]
    }

    if (Object.keys(nextA).length > 0) {
      root[a] = nextA
    } else {
      delete root[a]
    }
  } else {
    const [a, key] = parts
    const sectionA = root[a] as Section | undefined
    if (!sectionA || sectionA[key] === undefined) return design

    const nextA: Section = { ...sectionA }
    delete nextA[key]

    if (Object.keys(nextA).length > 0) {
      root[a] = nextA
    } else {
      delete root[a]
    }
  }

  return Object.keys(root).length > 0 ? (root as TableDesign) : undefined
}

/** Clears every single-value override. Conditional-formatting rules are cleared separately (they are a list, not a themed default). */
export function revertAllTableDesign(design: TableDesign | undefined): TableDesign | undefined {
  if (!design?.conditional_formatting || design.conditional_formatting.length === 0) return undefined
  return { conditional_formatting: design.conditional_formatting }
}

// ---------------------------------------------------------------------------
// Conditional-formatting rule list helpers
// ---------------------------------------------------------------------------

/** Appends a new rule to the end of the list — evaluated after all existing rules (first match still wins). */
export function addConditionalFormatRule(
  design: TableDesign | undefined,
  rule: TableConditionalFormatRule,
): TableDesign {
  const rules = design?.conditional_formatting ?? []
  return { ...design, conditional_formatting: [...rules, rule] }
}

/** Replaces the rule at `index`. Pure — returns a new TableDesign. */
export function updateConditionalFormatRule(
  design: TableDesign | undefined,
  index: number,
  rule: TableConditionalFormatRule,
): TableDesign {
  const rules = design?.conditional_formatting ?? []
  const next = rules.map((r, i) => (i === index ? rule : r))
  return { ...design, conditional_formatting: next }
}

/** Removes the rule at `index`. Prunes the key entirely once the list is empty, same "no empty tree" convention as the rest of this module. */
export function removeConditionalFormatRule(
  design: TableDesign | undefined,
  index: number,
): TableDesign | undefined {
  const rules = design?.conditional_formatting ?? []
  const next = rules.filter((_, i) => i !== index)

  if (next.length === 0) {
    const { conditional_formatting: _drop, ...rest } = design ?? {}
    return Object.keys(rest).length > 0 ? (rest as TableDesign) : undefined
  }
  return { ...design, conditional_formatting: next }
}
